import { Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTv } from "@fortawesome/pro-regular-svg-icons"

import { SERVICE_CENTER_ADMIN_ORGANIZATION_DEVICES_ROUTE } from "../../../../constants/routes"
import { ErrorDisplay } from "../../../../core/api/errorTypes"
import { RefetchIntervals } from "../../../../core/api/refetchIntervals"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { DeviceStatus, DeviceType } from "../../../entities/devices/deviceTypes"
import { useMatchEnvironment } from "../../../environment/envHooks"
import { EnvironmentType } from "../../../environment/envTypes"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import {
    fetchServiceCenterDevices,
    ServiceCenterDevicesQuery,
} from "../../admin/organization/devices/serviceCenterDevicesApi"
import type { DevicesServiceCenterResponse } from "../../admin/organization/devices/serviceCenterDevicesApi"

const useStyles = makeStyles()((theme) => ({
    pill: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".5rem",
        height: "2.5rem",
        padding: "0 1rem",
        borderRadius: "6.25rem",
        textDecoration: "none",
        whiteSpace: "nowrap",
    },
    online: {
        backgroundColor: theme.palette.tailwind.teal[50],
        color: theme.palette.tailwind.teal[700],
    },
    offline: {
        backgroundColor: theme.palette.tailwind.red[50],
        color: theme.palette.tailwind.red[700],
    },
    label: {
        fontWeight: theme.typography.fontWeightMedium,
    },
}))

type ScreensSummary = Readonly<{
    total: number
    offline: number
}>

const selectSummary = (data: DevicesServiceCenterResponse): ScreensSummary => ({
    total: data.devices.length,
    offline: data.devices.filter((device) => device.statusDetails?.status !== DeviceStatus.ONLINE).length,
})

type Props = Readonly<{
    serviceCenterId: number
}>

export function InfoScreensStatus({ serviceCenterId }: Props) {
    const { classes, cx } = useStyles()
    const isEnabled = useMatchEnvironment([EnvironmentType.Local, EnvironmentType.Test])

    const { data: summary } = useApiQuery(fetchServiceCenterDevices, {
        queryName: ServiceCenterDevicesQuery.DEVICES,
        dependencies: { serviceCenterId, type: DeviceType.DISPLAY },
        enabled: isEnabled,
        select: selectSummary,
        errorDisplay: ErrorDisplay.NONE,
        refetchInterval: RefetchIntervals.long,
    })

    if (!isEnabled || !summary || summary.total === 0) return null

    const hasOffline = summary.offline > 0

    return (
        <Link
            to={SERVICE_CENTER_ADMIN_ORGANIZATION_DEVICES_ROUTE}
            className={cx(classes.pill, hasOffline ? classes.offline : classes.online)}
        >
            <FontAwesomeIcon icon={faTv} />
            {hasOffline ? (
                <LocalizedStrict id="service-center-top-bar-info-screens-offline" vars={{ count: summary.offline }}>
                    <Typography variant="body2" component="span" className={classes.label}>
                        {summary.offline} screens offline
                    </Typography>
                </LocalizedStrict>
            ) : (
                <LocalizedStrict id="service-center-top-bar-info-screens-online">
                    <Typography variant="body2" component="span" className={classes.label}>
                        Info screens online
                    </Typography>
                </LocalizedStrict>
            )}
        </Link>
    )
}
